/**
 * Forgot Password Page
 *
 * Public, two-step password reset flow for admin dashboard users:
 *   1. 'request' — enter email; Cognito emails a verification code.
 *   2. 'confirm' — enter the code and a new password.
 * On success the user is redirected to the login page.
 */

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Shield, Loader2 } from 'lucide-react';
import { useForgotPassword, useConfirmForgotPassword } from '@/hooks/use-forgot-password';
import { cognitoPasswordField } from '@/lib/auth-schemas';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Request Form Schema
 * Step 1 — validates the email to send a reset code to.
 */
const requestSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
});

type RequestFormData = z.infer<typeof requestSchema>;

/**
 * Confirm Form Schema
 * Step 2 — validates the verification code and new password.
 * Password rules match the NEW_PASSWORD_REQUIRED challenge on the login page.
 */
const confirmSchema = z
  .object({
    confirmationCode: z
      .string()
      .refine(code => code.length === 6 && [...code].every(c => c >= '0' && c <= '9'), {
        message: 'Enter the 6-digit code from your email',
      }),
    newPassword: cognitoPasswordField,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ConfirmFormData = z.infer<typeof confirmSchema>;

// Seconds to disable "Resend code" after each send — throttles SES emails and
// stops a frustrated user from hammering the button into Cognito's own limit.
const RESEND_COOLDOWN_SECONDS = 30;

/**
 * Forgot Password Page Component
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const forgotPasswordMutation = useForgotPassword();
  const confirmForgotPasswordMutation = useConfirmForgotPassword();

  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown(seconds => Math.max(0, seconds - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const requestForm = useForm<RequestFormData>({
    resolver: zodResolver(requestSchema),
    defaultValues: { email: '' },
  });

  const confirmForm = useForm<ConfirmFormData>({
    resolver: zodResolver(confirmSchema),
    defaultValues: { confirmationCode: '', newPassword: '', confirmPassword: '' },
  });

  /**
   * Step 1 — Request a reset code.
   * The admin pool has preventUserExistenceErrors enabled, so this always
   * advances to step 2 on success, even for unknown emails.
   */
  const onRequestSubmit = async (data: RequestFormData) => {
    setError(null);
    try {
      await forgotPasswordMutation.mutateAsync({ email: data.email });
      setEmail(data.email);
      setStep('confirm');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code. Please try again.');
    }
  };

  /**
   * Step 2 — Confirm the code and set a new password.
   */
  const onConfirmSubmit = async (data: ConfirmFormData) => {
    setError(null);
    try {
      await confirmForgotPasswordMutation.mutateAsync({
        email,
        confirmationCode: data.confirmationCode,
        newPassword: data.newPassword,
      });

      toast({
        title: 'Password reset',
        description: 'You can now sign in with your new password.',
      });
      navigate('/login');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Password reset failed. Please try again.');
    }
  };

  /**
   * Resend the verification code to the same email.
   */
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setError(null);
    try {
      await forgotPasswordMutation.mutateAsync({ email });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      toast({
        title: 'Code resent',
        description: 'Check your email for a new verification code.',
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to resend code. Please try again.');
    }
  };

  /**
   * Return to the email step.
   */
  const handleBackToEmail = () => {
    setStep('request');
    setError(null);
    confirmForm.reset();
  };

  // Step 2 — confirm code + new password
  if (step === 'confirm') {
    const isLoading = confirmForgotPasswordMutation.isPending;
    // A resend is an independent in-flight request; block the whole form while
    // either it or the confirm submission is pending.
    const isBusy = isLoading || forgotPasswordMutation.isPending;

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <Shield className="h-12 w-12 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">Enter Verification Code</CardTitle>
            <CardDescription>
              We sent a code to {email}. Enter it below along with your new password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={confirmForm.handleSubmit(onConfirmSubmit)} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="confirmationCode">Verification Code</Label>
                <Input
                  id="confirmationCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="Enter the code from your email"
                  {...confirmForm.register('confirmationCode')}
                  disabled={isLoading}
                />
                {confirmForm.formState.errors.confirmationCode && (
                  <p className="text-sm text-red-600">
                    {confirmForm.formState.errors.confirmationCode.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <PasswordInput
                  id="newPassword"
                  autoComplete="new-password"
                  {...confirmForm.register('newPassword')}
                  disabled={isLoading}
                />
                {confirmForm.formState.errors.newPassword && (
                  <p className="text-sm text-red-600">
                    {confirmForm.formState.errors.newPassword.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Must be 8+ characters with uppercase, lowercase, number, and special character
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <PasswordInput
                  id="confirmPassword"
                  autoComplete="new-password"
                  {...confirmForm.register('confirmPassword')}
                  disabled={isLoading}
                />
                {confirmForm.formState.errors.confirmPassword && (
                  <p className="text-sm text-red-600">
                    {confirmForm.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isBusy}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  disabled={isBusy}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  Use a different email
                </button>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isBusy || resendCooldown > 0}
                  className="text-primary hover:underline disabled:opacity-50"
                >
                  {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 1 — request a reset code
  const isLoading = forgotPasswordMutation.isPending;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <Shield className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
          <CardDescription>
            Enter your email and we&apos;ll send you a verification code
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={requestForm.handleSubmit(onRequestSubmit)} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                {...requestForm.register('email')}
                disabled={isLoading}
                autoComplete="email"
              />
              {requestForm.formState.errors.email && (
                <p className="text-sm text-red-600">{requestForm.formState.errors.email.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Code'
              )}
            </Button>

            <div className="text-center text-sm">
              <Link to="/login" className="text-primary hover:underline">
                Back to sign in
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
